<script setup lang="ts">
import { AmountCurrencySelector } from '~/components/feature/amount-currency-selector'
import { useCreditFilters } from '~/components/widget/credits/list-meili-search/mocks'
import type { IMultiselectOption } from '~/types'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from '~/components/ui/select'
import { ToggleGroup, ToggleGroupItem } from '~/components/ui/toggle-group'
import { Checkbox } from '~/components/ui/checkbox'
import { Input } from '~/components/ui/input'

const { t } = useI18n()

const { periodOptions } = useCreditFilters()

// Filter models
const selectedAmount = defineModel<number | undefined>('amount')
const selectedCurrency = defineModel<string>('currency', { default: 'kgs' })
const selectedPeriod = defineModel<string>('period', { default: '12' })
const selectedForWhom = defineModel<string>('forWhom', { default: 'private_person' })
const selectedOrganizationType = defineModel<string[]>('organizationType', { default: () => [] })
const selectedIncomeConfirmation = defineModel<string>('incomeConfirmation', { default: 'any' })
const selectedGuarantee = defineModel<string>('guarantee', { default: 'any' })
const selectedCollateral = defineModel<string>('collateral', { default: 'any' })
const selectedInitialFee = defineModel<string>('initialFee', { default: 'any' })
const selectedDownPayment = defineModel<number | undefined>('downPayment')

const downPaymentPercentages = [10, 20, 30]

const setDownPaymentPercent = (percent: number) => {
  if (selectedAmount.value) {
    selectedDownPayment.value = Math.round((selectedAmount.value * percent) / 100)
  }
}

const currencyStore = useCurrencyStore()

const currencyOptions = computed<IMultiselectOption[]>(() => {
  const menuItems = ['KGS', 'RUB', 'USD', 'EUR', 'KZT', 'CNY']
  return [
    { id: 0, label: t('common.any_currency'), value: 'any' },
    ...currencyStore.list
      .filter((c) => menuItems.includes(c.code))
      .map((c, i) => ({
        id: i + 1,
        label: c.code,
        value: c.code.toLowerCase()
      }))
  ]
})

onMounted(() => {
  currencyStore.fetchCurrencies()
})
</script>

<template>
  <ContentBlock variant="white" class="sticky top-32 !p-4">
    <!-- Credit Amount with Currency Selector -->
    <div class="space-y-2">
      <label for="credit-amount" class="text-sm font-semibold text-gray-700">{{ $t('common.credit_amount') }}</label>
      <AmountCurrencySelector
        id="credit-amount"
        v-model:amount="selectedAmount"
        v-model:currency="selectedCurrency"
        :currency-options="currencyOptions"
      />
    </div>

    <!-- Down Payment -->
    <div class="space-y-2">
      <label for="down-payment" class="text-sm font-semibold text-gray-700">{{ $t('common.down_payment') }}</label>
      <Input
        id="down-payment"
        :model-value="selectedDownPayment ?? ''"
        type="number"
        step="any"
        class="w-full"
        :placeholder="$t('common.down_payment')"
        :disabled="!selectedAmount"
        @update:model-value="
          (val) => {
            selectedDownPayment = val !== '' ? parseFloat(String(val)) : undefined
          }
        "
      />
      <div class="flex gap-2">
        <button
          v-for="pct in downPaymentPercentages"
          :key="pct"
          type="button"
          class="flex-1 rounded-md border border-gray-200 py-1 text-xs font-medium text-gray-600 transition hover:border-blue-600 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="!selectedAmount"
          @click="setDownPaymentPercent(pct)"
        >
          {{ pct }}%
        </button>
      </div>
    </div>

    <!-- For Whom (Toggle Group) -->
    <div class="space-y-2">
      <label class="text-sm font-semibold text-gray-700">{{ $t('common.for_whom') }}</label>
      <ToggleGroup
        v-model="selectedForWhom"
        type="single"
        variant="outline"
        class="grid w-full grid-cols-2"
      >
        <ToggleGroupItem value="private_person" class="w-full">
          {{ $t('common.private_person') }}
        </ToggleGroupItem>
        <ToggleGroupItem value="legal_entity" class="w-full">
          {{ $t('common.legal_entity') }}
        </ToggleGroupItem>
      </ToggleGroup>
    </div>

    <!-- Credit Term (Select) -->
    <div class="space-y-2">
      <label class="text-sm font-semibold text-gray-700">{{ $t('common.credit_term') }}</label>
      <Select v-model="selectedPeriod">
        <SelectTrigger class="w-full" variant="searchForm" :aria-label="$t('common.select_period')">
          <SelectValue :placeholder="$t('common.select_period')" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="option in periodOptions" :key="option.id" :value="option.value">
            {{ option.label }}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>

    <!-- Organization Type (Toggle Group) -->
    <div class="space-y-2">
      <label class="text-sm font-semibold text-gray-700">{{
        $t('common.organization_label')
      }}</label>
      <ToggleGroup
        v-model="selectedOrganizationType"
        type="multiple"
        variant="outline"
        class="grid w-full grid-cols-2"
      >
        <ToggleGroupItem value="bank" class="w-full">{{ $t('common.banks') }}</ToggleGroupItem>
        <ToggleGroupItem value="microcredit_company" class="w-full">{{
          $t('common.microcredits')
        }}</ToggleGroupItem>
      </ToggleGroup>
    </div>

    <!-- Requirements group -->
    <div class="border-t border-gray-100 pt-4">
      <p class="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {{ $t('common.requirements') }}
      </p>
      <div class="space-y-3">
        <label class="flex cursor-pointer items-center justify-between gap-3">
          <span class="text-sm text-gray-700">
            {{ $t('widget.credits.list_meili_search.income_confirmation_placeholder') }}
          </span>
          <Checkbox
            :model-value="selectedIncomeConfirmation === '1'"
            @update:model-value="(v) => (selectedIncomeConfirmation = v ? '1' : 'any')"
          />
        </label>
        <label class="flex cursor-pointer items-center justify-between gap-3">
          <span class="text-sm text-gray-700">
            {{ $t('widget.credit_page.specifications.guarantor_required') }}
          </span>
          <Checkbox
            :model-value="selectedGuarantee === '1'"
            @update:model-value="(v) => (selectedGuarantee = v ? '1' : 'any')"
          />
        </label>
        <label class="flex cursor-pointer items-center justify-between gap-3">
          <span class="text-sm text-gray-700">
            {{ $t('widget.credits.list_meili_search.collateral_placeholder') }}
          </span>
          <Checkbox
            :model-value="selectedCollateral === '1'"
            @update:model-value="(v) => (selectedCollateral = v ? '1' : 'any')"
          />
        </label>
        <label class="flex cursor-pointer items-center justify-between gap-3">
          <span class="text-sm text-gray-700">
            {{ $t('widget.credit_page.specifications.initial_payment_required') }}
          </span>
          <Checkbox
            :model-value="selectedInitialFee === '1'"
            @update:model-value="(v) => (selectedInitialFee = v ? '1' : 'any')"
          />
        </label>
      </div>
    </div>
  </ContentBlock>
</template>
